import CtaButtonGroup, { CtaType } from "@/components/molecules/CtaButtonGroup";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Text from "./shared/Text";

/**
 * DarkReasonsToSwitchSection — full-bleed dark section listing 6 reasons
 * creators switch from a competitor to Graphy.
 *
 * Layout: centered header ("Reasons creators are switching from" white +
 * "<Competitor> to Graphy" muted-grey), primary CTA, then a 3x2 grid of
 * reason cards (dark glassy surface, circular white icon chip, title,
 * description).
 */

export type DarkReasonItem = {
  icon: IconDefinition;
  iconColorClass?: string;
  title: string;
  description: string;
};

export type DarkReasonsToSwitchData = {
  titleIntro: string;
  competitorName: string;
  subtitle?: string;
  primaryCta: CtaType;
  ctas?: CtaType[];
  reasons: DarkReasonItem[];
};

const DarkReasonsToSwitchSection = ({
  titleIntro,
  competitorName,
  subtitle,
  primaryCta,
  ctas,
  reasons,
}: DarkReasonsToSwitchData) => {
  return (
    <section className="relative w-full overflow-hidden bg-neutralBlack">
      {/* Radial glow decoration */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          background:
            "radial-gradient(ellipse 45% 55% at 10% 20%, rgba(138, 74, 255, 0.25) 0%, rgba(0, 0, 0, 0) 60%)," +
            "radial-gradient(ellipse 45% 55% at 90% 80%, rgba(255, 140, 60, 0.22) 0%, rgba(0, 0, 0, 0) 60%)",
        }}
      />

      <div className="container relative z-10 mx-auto px-4 py-10 sm:px-6 md:px-8 md:py-20 lg:px-12 lg:py-24">
        <div className="mx-auto max-w-6xl">
        <Text
          as="h2"
          variant="display-lg"
          className="mx-auto max-w-4xl text-center text-[28px] sm:text-[32px] md:text-[40px] lg:text-[48px]"
        >
          <span className="text-white">{titleIntro}</span>{" "}
          <span className="text-white">{competitorName}</span>
        </Text>
        {subtitle ? (
          <Text
            as="p"
            variant="body-lg"
            className="mx-auto mt-5 max-w-3xl text-center text-white/70"
          >
            {subtitle}
          </Text>
        ) : null}
        <div className="mt-8 flex items-center justify-center md:mt-10">
          <CtaButtonGroup
            ctas={ctas ?? [primaryCta]}
            gap="flex gap-3"
            fullWidthOnMobile={false}
          />
        </div>

        <div className="mt-12 grid grid-cols-1 gap-5 md:mt-16 md:grid-cols-2 md:gap-6 lg:grid-cols-3">
          {reasons.map((r) => (
            <div
              key={r.title}
              className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-sm md:p-8"
            >
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white">
                <FontAwesomeIcon
                  icon={r.icon}
                  className={`h-5 w-5 ${r.iconColorClass ?? "text-[#CA26A5]"}`}
                  aria-hidden="true"
                />
              </div>
              <Text
                as="h3"
                variant="title-md"
                className="mt-6 text-[18px] font-semibold text-white md:text-[20px]"
              >
                {r.title}
              </Text>
              <Text
                as="p"
                variant="body-md"
                className="mt-3 text-[14px] leading-relaxed text-white/70"
              >
                {r.description}
              </Text>
            </div>
          ))}
        </div>
      </div>
      </div>
    </section>
  );
};

export default DarkReasonsToSwitchSection;
